import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input, Select } from '@/components/ui';
import {
  confirmProbation,
  endProbation,
  extendProbation,
  submitProbationReview,
  type ProbationCase,
  type ProbationRecommendation,
} from '@/api/services/probation.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { cn } from '@/lib/cn';
import {
  daysUntil,
  formatDate,
  fromDateInput,
  RECOMMENDATION_LABELS,
  toDateInput,
} from './probationFormat';

export type ProbationAction = 'review' | 'extend' | 'confirm' | 'end';

const TEXTAREA_CLASS =
  'block w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 py-2.5 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500';
const LABEL_CLASS =
  'mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400';

function TextArea({
  id,
  label,
  value,
  onChange,
  required,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
}): React.ReactElement {
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      <textarea
        id={id}
        rows={3}
        value={value}
        aria-required={required || undefined}
        onChange={(event) => onChange(event.target.value)}
        className={TEXTAREA_CLASS}
      />
    </div>
  );
}

/**
 * Shared plumbing for the four case commands. The request id is minted once
 * per open form, so a retry after a network failure replays the same command
 * instead of applying it twice.
 */
function useCaseCommand<T>(
  companyId: string,
  caseId: string,
  run: (requestId: string, input: T) => Promise<ProbationCase>,
  successMessage: string,
  onDone: () => void
): {
  submit: (input: T) => void;
  isPending: boolean;
  error: string | null;
} {
  const [requestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: T) => run(requestId, input),
    onSuccess: () => {
      toast.success(successMessage);
      for (const key of [
        ['hr-probation-cases'],
        ['hr-probation-summary'],
        ['hr-probation-reviewing'],
        ['hr-probation-case', companyId, caseId],
        ['hr-employee-profile'],
        ['hr-employee-timeline'],
        ['hr-employments'],
      ]) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      onDone();
    },
    onError: (mutationError) => {
      // Policy refusals (409) and the self-review guard (403) stay in the form.
      const message = getErrorMessage(mutationError);
      setError(message);
      toast.error(message);
    },
  });
  return {
    submit: (input) => {
      setError(null);
      mutation.mutate(input);
    },
    isPending: mutation.isPending,
    error,
  };
}

function FormShell({
  title,
  error,
  children,
  onCancel,
  onSubmit,
  submitLabel,
  submitVariant = 'primary',
  disabled,
  isPending,
}: {
  title: string;
  error: string | null;
  children: React.ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel: string;
  submitVariant?: 'primary' | 'danger';
  disabled: boolean;
  isPending: boolean;
}): React.ReactElement {
  return (
    <form
      className="space-y-4 rounded-xl border border-gray-200 bg-gray-50/60 p-4 dark:border-gray-700 dark:bg-gray-800/40"
      aria-label={title}
      onSubmit={(event) => {
        event.preventDefault();
        if (!disabled && !isPending) onSubmit();
      }}
    >
      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h4>
      {children}
      {error && (
        <p className="text-sm text-danger" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          size="sm"
          variant={submitVariant}
          disabled={disabled || isPending}
          loading={isPending}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

const RATINGS = [1, 2, 3, 4, 5] as const;

export function ReviewForm({
  companyId,
  probationCase,
  onDone,
}: {
  companyId: string;
  probationCase: ProbationCase;
  onDone: () => void;
}): React.ReactElement {
  const existing = probationCase.review;
  const [rating, setRating] = useState<number | null>(existing?.rating || null);
  const [strengths, setStrengths] = useState(existing?.strengths ?? '');
  const [improvements, setImprovements] = useState(existing?.improvements ?? '');
  const [recommendation, setRecommendation] = useState<ProbationRecommendation>(
    existing?.recommendation ?? 'confirm'
  );
  const command = useCaseCommand(
    companyId,
    probationCase._id,
    (requestId, input: Omit<Parameters<typeof submitProbationReview>[2], 'requestId'>) =>
      submitProbationReview(companyId, probationCase._id, { requestId, ...input }),
    'Review submitted',
    onDone
  );
  const ready = rating !== null && strengths.trim() !== '' && improvements.trim() !== '';

  return (
    <FormShell
      title={existing ? 'Replace review' : 'Submit review'}
      error={command.error}
      onCancel={onDone}
      submitLabel={existing ? 'Replace review' : 'Submit review'}
      disabled={!ready}
      isPending={command.isPending}
      onSubmit={() =>
        rating !== null &&
        command.submit({
          rating,
          strengths: strengths.trim(),
          improvements: improvements.trim(),
          recommendation,
        })
      }
    >
      {existing && (
        <p className="text-xs text-gray-600 dark:text-gray-300">
          A new review replaces the current one. The change is audited.
        </p>
      )}
      <fieldset>
        <legend className={LABEL_CLASS}>
          Rating<span className="text-danger"> *</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {RATINGS.map((value) => {
            const selected = rating === value;
            return (
              <label
                key={value}
                className={cn(
                  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg border text-sm font-medium transition-colors has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-primary',
                  selected
                    ? 'border-primary/50 bg-primary/10 text-primary dark:text-sky-400'
                    : 'border-gray-200 text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700/50'
                )}
              >
                <input
                  type="radio"
                  name="probation-rating"
                  value={value}
                  checked={selected}
                  onChange={() => setRating(value)}
                  className="sr-only"
                  aria-label={`${value} out of 5`}
                />
                <span aria-hidden>{value}</span>
              </label>
            );
          })}
        </div>
      </fieldset>
      <TextArea
        id="probation-strengths"
        label="Strengths"
        value={strengths}
        onChange={setStrengths}
        required
      />
      <TextArea
        id="probation-improvements"
        label="Areas to improve"
        value={improvements}
        onChange={setImprovements}
        required
      />
      <Select
        id="probation-recommendation"
        label="Recommendation"
        value={recommendation}
        clearable={false}
        onChange={(value) =>
          setRecommendation(value === 'extend' || value === 'end' ? value : 'confirm')
        }
        options={(Object.keys(RECOMMENDATION_LABELS) as ProbationRecommendation[]).map(
          (value) => ({ value, label: RECOMMENDATION_LABELS[value] })
        )}
      />
    </FormShell>
  );
}

export function ExtendForm({
  companyId,
  probationCase,
  maxExtensionDays,
  onDone,
}: {
  companyId: string;
  probationCase: ProbationCase;
  /** `null` when the limit is unknown; the server still enforces it. */
  maxExtensionDays: number | null;
  onDone: () => void;
}): React.ReactElement {
  const [newEndDate, setNewEndDate] = useState('');
  const [reason, setReason] = useState('');
  const command = useCaseCommand(
    companyId,
    probationCase._id,
    (requestId, input: { newEndDate: string; reason: string }) =>
      extendProbation(companyId, probationCase._id, { requestId, ...input }),
    'Probation extended',
    onDone
  );

  const currentEnd = toDateInput(probationCase.currentEndDate);
  // Days the chosen date adds on top of the current end date.
  const added =
    newEndDate && currentEnd
      ? daysUntil(fromDateInput(newEndDate), new Date(fromDateInput(currentEnd)))
      : null;
  let dateError: string | undefined;
  if (added !== null && added <= 0) {
    dateError = `Choose a date after the current end date (${formatDate(probationCase.currentEndDate)}).`;
  } else if (added !== null && maxExtensionDays !== null && added > maxExtensionDays) {
    dateError = `An extension can add at most ${maxExtensionDays} days to the current end date.`;
  }
  const reasonValid = reason.trim().length >= 3;
  const ready = newEndDate !== '' && !dateError && reasonValid;

  return (
    <FormShell
      title="Extend probation"
      error={command.error}
      onCancel={onDone}
      submitLabel="Extend"
      disabled={!ready}
      isPending={command.isPending}
      onSubmit={() =>
        command.submit({ newEndDate: fromDateInput(newEndDate), reason: reason.trim() })
      }
    >
      <p className="text-xs text-gray-600 dark:text-gray-300">
        Current end date {formatDate(probationCase.currentEndDate)}
        {maxExtensionDays !== null && ` · at most ${maxExtensionDays} days per extension`}
      </p>
      <Input
        id="probation-new-end"
        label="New end date"
        type="date"
        required
        min={currentEnd || undefined}
        value={newEndDate}
        error={dateError}
        onChange={(event) => setNewEndDate(event.target.value)}
      />
      <TextArea
        id="probation-extend-reason"
        label="Reason (at least 3 characters)"
        value={reason}
        onChange={setReason}
        required
      />
    </FormShell>
  );
}

export function ConfirmForm({
  companyId,
  probationCase,
  onDone,
}: {
  companyId: string;
  probationCase: ProbationCase;
  onDone: () => void;
}): React.ReactElement {
  const [effectiveDate, setEffectiveDate] = useState('');
  const [comment, setComment] = useState('');
  const command = useCaseCommand(
    companyId,
    probationCase._id,
    (requestId, input: { effectiveDate?: string; comment?: string }) =>
      confirmProbation(companyId, probationCase._id, { requestId, ...input }),
    'Employment confirmed',
    onDone
  );

  return (
    <FormShell
      title="Confirm employment"
      error={command.error}
      onCancel={onDone}
      submitLabel="Confirm employment"
      disabled={false}
      isPending={command.isPending}
      onSubmit={() =>
        command.submit({
          ...(effectiveDate && { effectiveDate: fromDateInput(effectiveDate) }),
          ...(comment.trim() && { comment: comment.trim() }),
        })
      }
    >
      <p className="text-xs text-gray-600 dark:text-gray-300">
        Confirmation closes the case and moves the employment out of probation.
      </p>
      <Input
        id="probation-confirm-date"
        label="Effective date (optional, defaults to today)"
        type="date"
        value={effectiveDate}
        onChange={(event) => setEffectiveDate(event.target.value)}
      />
      <TextArea
        id="probation-confirm-comment"
        label="Comment (optional)"
        value={comment}
        onChange={setComment}
      />
    </FormShell>
  );
}

export function EndForm({
  companyId,
  probationCase,
  onDone,
}: {
  companyId: string;
  probationCase: ProbationCase;
  onDone: () => void;
}): React.ReactElement {
  const [reason, setReason] = useState('');
  const [lastWorkingDate, setLastWorkingDate] = useState('');
  const command = useCaseCommand(
    companyId,
    probationCase._id,
    (requestId, input: { reason: string; lastWorkingDate: string }) =>
      endProbation(companyId, probationCase._id, { requestId, ...input }),
    'Probation ended',
    onDone
  );
  const ready = reason.trim().length >= 3 && lastWorkingDate !== '';

  return (
    <FormShell
      title="End probation"
      error={command.error}
      onCancel={onDone}
      submitLabel="End probation"
      submitVariant="danger"
      disabled={!ready}
      isPending={command.isPending}
      onSubmit={() =>
        command.submit({
          reason: reason.trim(),
          lastWorkingDate: fromDateInput(lastWorkingDate),
        })
      }
    >
      <p className="text-xs text-gray-600 dark:text-gray-300">
        Ending probation starts the employee&apos;s exit with this last working date.
      </p>
      <Input
        id="probation-last-working"
        label="Last working date"
        type="date"
        required
        value={lastWorkingDate}
        onChange={(event) => setLastWorkingDate(event.target.value)}
      />
      <TextArea
        id="probation-end-reason"
        label="Reason (at least 3 characters)"
        value={reason}
        onChange={setReason}
        required
      />
    </FormShell>
  );
}
